import {
  telemetryInternalIds,
  telemetryIdsFromUrl,
} from './telemetry-identifiers';
import { inject, Injectable, effect } from '@angular/core';
import * as Sentry from '@sentry/angular';
import { AuthService } from '@brightside/core/auth';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { StructuredLogger } from './structured-logger.service';
import { safeSentryExtras, sentryRoute } from './sentry-data-policy';
import { requestIdFor, requestStatusFor } from './request-correlation';

@Injectable({ providedIn: 'root' })
export class SentryService {
  private _authService = inject(AuthService);
  private _userContextGeneration = 0;
  private readonly _logs = inject(StructuredLogger);
  private readonly _reportedErrors = new WeakSet<object>();
  private readonly _reportedRequests = new Set<string>();

  constructor() {
    this._authService.diagnostics$
      .pipe(takeUntilDestroyed())
      .subscribe((diagnostic) => {
        if (diagnostic.kind === 'token_refresh_failed') {
          this._logs.emit('auth.token_refresh.failed', {
            feature: 'authentication',
            stage: 'token_refresh',
            outcome: 'failed',
            reason_code: diagnostic.reason,
            duration_ms: diagnostic.durationMs,
          });
        } else {
          this._logs.emit('auth.session.forced_logout', {
            feature: 'authentication',
            stage: 'sign_out',
            outcome: 'requested',
            reason_code: diagnostic.reason,
          });
        }
      });
    // Reactively respond to auth state changes
    effect(() => {
      const isAuth = this._authService.loggedIn();
      this._authService.user();

      if (isAuth) {
        this._setUserContext();
      } else {
        this._clearUserContext();
      }
    });
  }

  private async _setUserContext(): Promise<void> {
    const generation = ++this._userContextGeneration;
    this._resetSentryUser();
    try {
      const user = await this._authService.getUser();
      if (
        !user ||
        generation !== this._userContextGeneration ||
        !this._authService.loggedIn()
      )
        return;

      Sentry.setUser({
        id: user.sub,
        email: user.email,
        username: user.preferred_username ?? user.name,
      });

      const groups = this._authService.getUserGroups();
      const groupNames = groups.length ? groups.join(',') : undefined;
      Sentry.setTag('user_groups', groupNames);
      // Tags enrich errors; log attributes enrich structured logs.
      Sentry.getIsolationScope().setAttribute('user.groups', groupNames);
    } catch (err) {
      console.warn('Failed to set Sentry user context:', err);
    }
  }

  private _clearUserContext(): void {
    ++this._userContextGeneration;
    this._resetSentryUser();
  }

  private _resetSentryUser(): void {
    Sentry.setUser(null);
    Sentry.setTag('user_groups', undefined);
    Sentry.getIsolationScope().setAttribute('user.groups', undefined);
  }

  captureMessage(_msg: string, level: Sentry.SeverityLevel = 'info'): void {
    Sentry.captureMessage('Application operation failed', level);
  }

  captureError(err: unknown): void {
    this.captureErrorWithExtras(err, {});
  }

  /** API owns HTTP exceptions; feature callers may report otherwise unreported errors. */
  captureErrorWithExtras(err: unknown, extras: Record<string, unknown>): void {
    if (!this._claimError(err)) return;
    Sentry.withScope((scope) => {
      for (const [key, value] of Object.entries(
        safeSentryExtras({
          ...extras,
          http_status: this._errorStatus(err),
          request_id: requestIdFor(err),
        }),
      )) {
        scope.setExtra(key, value);
      }
      const context = safeSentryExtras(extras);
      if (context['method'] && context['route_template'])
        scope.setFingerprint([
          '{{default}}',
          String(context['method']),
          String(context['route_template']),
          String(this._errorStatus(err) ?? 'unknown'),
        ]);
      Sentry.captureException(
        err instanceof Error
          ? err
          : new Error(
              this._errorStatus(err) !== undefined
                ? 'HTTP request failed'
                : 'Application operation failed',
            ),
      );
    });
  }

  private _errorStatus(err: unknown): number | undefined {
    try {
      return requestStatusFor(err);
    } catch {
      return undefined;
    }
  }

  private _claimError(err: unknown): boolean {
    if (
      err !== null &&
      (typeof err === 'object' || typeof err === 'function')
    ) {
      if (this._reportedErrors.has(err)) return false;
      this._reportedErrors.add(err);
    }
    const requestId = requestIdFor(err);
    if (requestId) {
      if (this._reportedRequests.has(requestId)) return false;
      this._reportedRequests.add(requestId);
      // Bound memory while covering UI catches/replacements of recent failures.
      if (this._reportedRequests.size > 500) {
        const oldest = this._reportedRequests.values().next().value;
        if (oldest !== undefined) this._reportedRequests.delete(oldest);
      }
    }
    return true;
  }

  setTag(key: string, value: string): void {
    Sentry.setTag(key, value);
  }

  setContext(name: string, context: Record<string, unknown>): void {
    Sentry.setContext(name, context);
  }

  captureApiError(params: {
    error: unknown;
    path: string;
    method: string;
    apiName?: string;
    clientGuid?: string;
    identifiers?: Record<string, unknown>;
    handled?: boolean;
  }) {
    const { error, path, method, clientGuid, handled = false } = params;
    const identifiers = telemetryInternalIds({
      ...telemetryIdsFromUrl(path),
      ...(clientGuid ? { client_guid: clientGuid } : {}),
      ...params.identifiers,
    });

    if (handled) {
      // Expected API outcomes must not be re-reported by a downstream UI catch.
      this._claimError(error);
      return;
    }

    Sentry.addBreadcrumb({
      category: 'api',
      message: 'HTTP request',
      level: 'error',
      data: {
        path: sentryRoute(path),
        method,
        status_code: this._errorStatus(error),
        ...identifiers,
      },
    });

    this.captureErrorWithExtras(error, {
      method,
      route_template: sentryRoute(path),
      ...identifiers,
    });
  }

  addBreadcrumb(params: {
    category: string;
    message: string;
    data?: Record<string, unknown>;
    level?: Sentry.SeverityLevel;
    type?: Sentry.Breadcrumb['type'];
  }): void {
    Sentry.addBreadcrumb({
      category: params.category,
      message: params.message,
      level: params.level ?? 'info',
      data: params.data,
      type: params.type ?? 'default',
      timestamp: Date.now() / 1000, // optional, Sentry auto-sets if omitted
    });
  }
}
